import type { Editor } from "@tiptap/react";
import type { LucideIcon } from "lucide-react";
import type { LinkData } from "replo-runtime/shared/types";

import * as React from "react";

import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import DeprecatedLabeledControl from "@v1/components/common/designSystem/DeprecatedLabeledControl";
import InputComponent from "@v1/components/common/designSystem/Input";
import InlineAlert from "@v1/components/designSystem/alert/InlineAlert";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import Popover from "@v1/components/designSystem/popover/Popover";
import SwitchWithDescription from "@v1/components/designSystem/switch/SwitchWithDescription";
import { selectOpenPopoverId, setOpenPopoverId } from "@v1/reducers/ui-reducer";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import { isSelectionEmpty } from "@v1/utils/rte";
import { Link } from "lucide-react";

export const ToolbarButton = ({
  icon,
  onClick,
  tooltipText,
  isActive,
}: {
  icon: LucideIcon;
  onClick?: () => void;
  tooltipText: string;
  isActive: boolean;
}) => {
  const Icon = icon;
  return (
    <Tooltip triggerAsChild content={tooltipText}>
      <button
        className={twMerge(
          "flex items-center justify-center bg-transparent p-0",
          isActive && "bg-primary rounded-xs text-white",
        )}
        onClick={onClick}
      >
        <Icon size={14} />
      </button>
    </Tooltip>
  );
};

const LinkEditorPopoverContent: React.FC<
  React.PropsWithChildren<{
    editor: Editor;
    onSubmit: (linkData: LinkData) => void;
    onCancel: () => void;
  }>
> = ({ editor, onSubmit, onCancel }) => {
  // Note (Noah, 2025-10-22): This is specifically a state, and not just a computed variable,
  // because if this component happens to rerender if the selection were to change, we would
  // not want it to update - we want this to represent the selection state at the time when
  // the component was mounted, because that better indicates whether the user was selecting
  // a non-empty block of text when this popover content started showing
  const [isEditorSelectionEmpty] = React.useState(isSelectionEmpty(editor));
  const [linkData, setLinkData] = React.useState<LinkData>({
    url: editor.getAttributes("link").href,
    isUnderlined: editor.isActive("underline") ?? true,
    isNewTab: editor.getAttributes("link").target === "_blank",
  });
  if (isEditorSelectionEmpty && !linkData.url) {
    return (
      <InlineAlert variant="warning" multiline>
        No text selected. Please select a block of text to create a link.
      </InlineAlert>
    );
  }
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(linkData);
      }}
      className="flex flex-col gap-4"
    >
      <DeprecatedLabeledControl label="URL" size="sm">
        <InputComponent
          size="sm"
          value={linkData.url}
          onChange={(e) => {
            return setLinkData((prevData) => ({
              ...prevData,
              url: e.target.value,
            }));
          }}
          autoFocus
        />
      </DeprecatedLabeledControl>
      <div className="flex flex-col gap-2">
        <SwitchWithDescription
          isOn={linkData.isUnderlined}
          onChange={() => {
            return setLinkData((prevData) => ({
              ...prevData,
              isUnderlined: !prevData.isUnderlined,
            }));
          }}
          size="sm"
          label="Underline the link content"
        />
        <SwitchWithDescription
          isOn={linkData.isNewTab}
          onChange={() => {
            return setLinkData((prevData) => ({
              ...prevData,
              isNewTab: !prevData.isNewTab,
            }));
          }}
          size="sm"
          label="Open Link In New Tab"
        />
      </div>
      <div className="flex flex-row items-center justify-end gap-2">
        <DeprecatedButton
          variant="secondary"
          onClick={() => {
            onCancel();
          }}
        >
          Cancel
        </DeprecatedButton>
        <DeprecatedButton variant="primary" type="submit">
          Save
        </DeprecatedButton>
      </div>
    </form>
  );
};

const TipTapToolbarLink: React.FC<
  React.PropsWithChildren<{
    editor: Editor | null;
    isActive: boolean;
  }>
> = ({ editor, isActive }) => {
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const openPopoverId = useEditorSelector(selectOpenPopoverId);
  const dispatch = useEditorDispatch();
  const isOpenedFromGlobalContext = openPopoverId === "tiptap-toolbar-link";

  const setLinkInEditor = React.useCallback(
    (linkData: LinkData) => {
      const { url, isUnderlined, isNewTab } = linkData;
      const wasPreviouslyUnderlined = editor?.isActive("underline");
      let focusedChain = editor?.chain().focus();
      // If url was somehow null (should never be the case) then just don't do anything
      if (url === null) {
        return;
      }
      if (url === "") {
        // If url was empty, we want to unset link from the whole range to remove the link
        focusedChain = focusedChain?.extendMarkRange("link").unsetLink();
      } else {
        // If url was provided, then take whatever link we're currently on, and update it
        focusedChain = focusedChain?.extendMarkRange("link").setLink({
          href: url,
          target: isNewTab ? "_blank" : undefined,
        });
      }

      if (isUnderlined && !wasPreviouslyUnderlined) {
        focusedChain = focusedChain?.setUnderline();
      } else if (!isUnderlined && wasPreviouslyUnderlined) {
        focusedChain = focusedChain?.unsetUnderline();
      }

      focusedChain?.run();
    },
    [editor],
  );

  if (!editor) {
    return null;
  }

  return (
    <Popover.Root
      isOpen={isPopoverOpen || isOpenedFromGlobalContext}
      onOpenChange={(newOpen) => {
        if (isOpenedFromGlobalContext) {
          // Note (Noah, 2025-10-22): We're guaranteed that newOpen is false in this case
          dispatch(setOpenPopoverId(null));
        } else {
          setIsPopoverOpen(newOpen);
        }
      }}
    >
      <Tooltip content="Set Link" triggerAsChild>
        <Popover.Trigger asChild>
          <button
            className={twMerge(
              "bg-surface flex items-center justify-center",
              isActive && "bg-primary rounded-xs text-white",
            )}
          >
            <Link size={14} />
          </button>
        </Popover.Trigger>
      </Tooltip>
      <Popover.Content
        shouldPreventDefaultOnInteractOutside={false}
        title="Configure Link"
        onRequestClose={() => {
          if (isOpenedFromGlobalContext) {
            dispatch(setOpenPopoverId(null));
          }
        }}
        onInteractOutside={() => {
          if (isOpenedFromGlobalContext) {
            dispatch(setOpenPopoverId(null));
          }
        }}
      >
        <LinkEditorPopoverContent
          editor={editor}
          onSubmit={setLinkInEditor}
          onCancel={() => {
            if (isOpenedFromGlobalContext) {
              dispatch(setOpenPopoverId(null));
            } else {
              setIsPopoverOpen(false);
            }
          }}
        />
      </Popover.Content>
    </Popover.Root>
  );
};

export default TipTapToolbarLink;
